import {
  publishToast,
  type AppFeatureDefinition,
  type FeatureRegistry,
  type RemoteFeatureFailure,
  type RemoteFeatureLoader,
  type RemoteFeatureLoadResult,
} from "@nasam/core";

export interface LoadRuntimeModulesOptions {
  readonly registry: FeatureRegistry;
  readonly loader?: RemoteFeatureLoader;
  readonly onFeatureFailure?: (failure: RemoteFeatureFailure) => void;
  readonly onManifestFailure?: (error: Error) => void;
  /**
   * Called with the definitions that loaded successfully. The host uses this to
   * register each module's own i18n bundle, which is what lets a remote ship
   * its strings without a host rebuild (REBUILD-PLAN §5.2).
   */
  readonly onFeaturesLoaded?: (
    modules: readonly AppFeatureDefinition[],
  ) => void | Promise<void>;
}

function toError(cause: unknown): Error {
  return cause instanceof Error ? cause : new Error(String(cause));
}

function reportModuleFailure(failure: RemoteFeatureFailure): void {
  publishToast({
    kind: "error",
    title: failure.featureId,
    description: failure.error.message,
  });
}

/**
 * Starts tenant-module discovery without making it a prerequisite for the
 * shell. A bad manifest or an unavailable remote must leave the host usable.
 */
export async function loadRuntimeFeatures({
  loader,
  onFeatureFailure,
  onManifestFailure,
  onFeaturesLoaded,
}: LoadRuntimeModulesOptions): Promise<RemoteFeatureLoadResult> {
  if (loader === undefined) {
    return { loaded: [], failed: [] };
  }

  try {
    const result = await loader.load();

    for (const failure of result.failed) {
      reportModuleFailure(failure);
      onFeatureFailure?.(failure);
    }

    // A remote that cannot ship its own strings would still need a host
    // rebuild, which is exactly what §5.2 puts `i18n` on the contract to avoid.
    if (result.loaded.length > 0) {
      await onFeaturesLoaded?.(result.loaded);
    }

    return result;
  } catch (cause) {
    const error = toError(cause);
    console.error(error);
    onManifestFailure?.(error);
    return { loaded: [], failed: [] };
  }
}
